import { useCallback, useEffect, useRef } from "react";

/** Region in container px (may extend past the container, e.g. on the desktop canvas). */
export interface SketchRect {
  x: number;
  y: number;
  w: number;
  h: number;
}

/** Keeps the bitmap under Safari's canvas area limit (~16.7M px) on huge surfaces. */
const MAX_CANVAS_AREA = 16_000_000;

export interface Stroke {
  color: string;
  size: number;
  eraser?: boolean;
  points: { x: number; y: number }[];
}

/**
 * Freehand drawing surface that overlays the journal canvas (Apple Preview–style
 * markup). Sits inside the journal container so it spans the full entry height
 * and is captured on export. Only intercepts pointer events while `active`.
 *
 * Strokes are stored in container px. `extend` grows the drawing surface beyond the
 * container (the desktop canvas passes the visible area and existing strokes), so the
 * user can draw anywhere they have panned to. Pointer positions are mapped through the
 * element's rendered size, so drawing stays accurate when an ancestor is zoomed.
 */
export function SketchCanvas({
  active,
  color,
  size,
  eraser,
  strokes,
  onCommitStroke,
  containerRef,
  extend,
}: {
  active: boolean;
  color: string;
  size: number;
  eraser: boolean;
  strokes: Stroke[];
  onCommitStroke: (s: Stroke) => void;
  containerRef: React.RefObject<HTMLDivElement | null>;
  extend?: SketchRect | null;
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const dims = useRef({ x: 0, y: 0, w: 0, h: 0, dpr: 1 });
  const drawing = useRef(false);
  const current = useRef<Stroke | null>(null);

  const strokePath = (ctx: CanvasRenderingContext2D, s: Stroke) => {
    if (!s.points.length) return;
    ctx.globalCompositeOperation = s.eraser ? "destination-out" : "source-over";
    ctx.strokeStyle = s.color;
    ctx.lineWidth = s.size;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.beginPath();
    ctx.moveTo(s.points[0].x, s.points[0].y);
    for (let i = 1; i < s.points.length; i++) ctx.lineTo(s.points[i].x, s.points[i].y);
    // A single tap still leaves a dot.
    if (s.points.length === 1) ctx.lineTo(s.points[0].x + 0.01, s.points[0].y + 0.01);
    ctx.stroke();
  };

  const redraw = useCallback(() => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext("2d");
    if (!canvas || !ctx) return;
    const { x, y, w, h, dpr } = dims.current;
    ctx.setTransform(dpr, 0, 0, dpr, -x * dpr, -y * dpr);
    ctx.clearRect(x, y, w, h);
    for (const s of strokes) strokePath(ctx, s);
    if (current.current) strokePath(ctx, current.current);
    ctx.globalCompositeOperation = "source-over";
  }, [strokes]);

  const measure = useCallback(() => {
    const canvas = canvasRef.current;
    const cont = containerRef.current;
    if (!canvas || !cont) return;
    // With `extend`, measure the container's own box: its scrollHeight would include this
    // (absolutely positioned, possibly extended) canvas and so could never shrink back.
    let x = 0, y = 0, w = cont.clientWidth, h = extend ? cont.clientHeight : cont.scrollHeight;
    if (extend) {
      const x2 = Math.max(w, extend.x + extend.w);
      const y2 = Math.max(h, extend.y + extend.h);
      x = Math.floor(Math.min(0, extend.x));
      y = Math.floor(Math.min(0, extend.y));
      w = Math.ceil(x2 - x);
      h = Math.ceil(y2 - y);
    }
    const dpr = Math.min(window.devicePixelRatio || 1, 2, Math.sqrt(MAX_CANVAS_AREA / Math.max(1, w * h)));
    dims.current = { x, y, w, h, dpr };
    canvas.width = Math.round(w * dpr);
    canvas.height = Math.round(h * dpr);
    canvas.style.left = `${x}px`;
    canvas.style.top = `${y}px`;
    canvas.style.width = `${w}px`;
    canvas.style.height = `${h}px`;
    redraw();
  }, [containerRef, redraw, extend?.x, extend?.y, extend?.w, extend?.h]); // eslint-disable-line react-hooks/exhaustive-deps

  // Re-measure on container resize / content growth.
  useEffect(() => {
    const cont = containerRef.current;
    if (!cont) return;
    measure();
    const ro = new ResizeObserver(() => measure());
    ro.observe(cont);
    window.addEventListener("resize", measure);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, [containerRef, measure]);

  useEffect(() => { redraw(); }, [redraw]);

  const posFromEvent = (e: React.PointerEvent) => {
    const r = canvasRef.current!.getBoundingClientRect();
    const { x, y, w, h } = dims.current;
    return { x: x + (e.clientX - r.left) * (w / r.width), y: y + (e.clientY - r.top) * (h / r.height) };
  };

  const handleDown = (e: React.PointerEvent) => {
    if (!active) return;
    e.preventDefault();
    (e.currentTarget as Element).setPointerCapture?.(e.pointerId);
    drawing.current = true;
    current.current = { color, size, eraser, points: [posFromEvent(e)] };
    redraw();
  };

  const handleMove = (e: React.PointerEvent) => {
    if (!active || !drawing.current || !current.current) return;
    current.current.points.push(posFromEvent(e));
    redraw();
  };

  const handleUp = () => {
    if (!drawing.current) return;
    drawing.current = false;
    const s = current.current;
    current.current = null;
    if (s && s.points.length) onCommitStroke(s);
    else redraw();
  };

  return (
    <canvas
      ref={canvasRef}
      className="absolute left-0 top-0"
      data-canvas-item={active ? "" : undefined}
      style={{
        zIndex: 30,
        pointerEvents: active ? "auto" : "none",
        cursor: active ? "crosshair" : "default",
        touchAction: "none",
      }}
      onPointerDown={handleDown}
      onPointerMove={handleMove}
      onPointerUp={handleUp}
      onPointerLeave={handleUp}
    />
  );
}
